import './rankings-view.styles.scss';
import { Link } from 'react-router-dom';
import { quadCutoffs } from '@shared/quads.js';
import { recordText, streakText } from '@/utils/leagueLabels';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import ManagerIcon from '@/components/SHARED/manager-icon/manager-icon.component';
import HelpLink from '@/components/SHARED/help-link/help-link.component';

// College: the Top 25 ranking, plus the results-only team rating and quad
// records Selection Day uses. Pro: Power Rankings.
// rankings: summary/rankings; teams: summary/teams map.
const RankingsView = ({ league, rankings, teams, teamLink, highlightId }) => {

    if (!rankings || !teams) {
        return <div className='g-card'><p className='meta'>Rankings appear once the league is created.</p></div>;
    }

    const pro = league?.type === 'pro';
    const rows = Object.entries(rankings.teams)
        .map(([id, r]) => ({ ...r, id, team: teams[id] }))
        .filter(r => r.team)
        .sort((a, b) => a.poll - b.poll);
    const played = rankings.day > 0 && rows.some(r => r.rating !== undefined);
    const cut = quadCutoffs(rows.length);
    const range = (q) => {
        const text = (list) => (q === 3 ? `${list[2] + 1}+` : `${q === 0 ? 1 : list[q - 1] + 1}-${list[q]}`);
        return `home ${text(cut.home)}, neutral ${text(cut.neutral)}, away ${text(cut.away)}`;
    };
    const move = (r) => {
        if (!r.prev || r.prev === r.poll) return <span className='move same' aria-label='No change'>–</span>;
        const up = r.prev > r.poll;
        return (
            <span className={`move ${up ? 'up' : 'down'}`} aria-label={`${up ? 'Up' : 'Down'} ${Math.abs(r.prev - r.poll)}`}>
                {up ? '▲' : '▼'}{Math.abs(r.prev - r.poll)}
            </span>
        );
    };
    const teamCell = (r) => (
        <Link to={teamLink(r.id)} className='team-link'>
            <TeamBadge team={r.team} size='sm' />
            <span>{r.team.name}<ManagerIcon team={r.team} /></span>
        </Link>
    );

    return (
        <div className='rankings-view'>
            <div className='g-card'>
                <h3>{pro ? 'Power Rankings' : 'Rankings'} <HelpLink section='rankings' label='How rankings work' /></h3>
                <p className='meta'>
                    {rankings.day === 0 ? 'Preseason: ' : `After day ${rankings.day}: `}
                    teams start from a preseason projection (player ability, chemistry grade{pro ? '' : ', program prestige'} and
                    schedule), and results count for more as the season goes on.
                    {pro ? '' : ' The Top 25 appear next to team names around the league.'}
                </p>
                {!pro &&
                    <>
                        <p className='meta'>
                            Selection Day doesn&apos;t use the Top 25. It uses the <b>rating</b> (results only, adjusted for opponents and home court)
                            and the quad records.
                        </p>
                        <ul className='quad-key meta'>
                            {[0, 1, 2, 3].map(q => <li key={q}><b>Q{q + 1}</b>: opponent rated {range(q)}</li>)}
                        </ul>
                    </>
                }
            </div>
            <div className='g-card'>
                <div className='table-scroll'>
                    <table className='g-table rankings-table'>
                        <thead>
                            <tr>
                                <th className='num'>#</th>
                                <th>Team</th>
                                <th className='num'>Record</th>
                                {pro && <th className='num'>Streak</th>}
                                {!pro && <th className='num' title='Results-only rating rank, used on Selection Day'>Rating</th>}
                                {!pro && <th className='num'>Q1</th>}
                                {!pro && <th className='num'>Q2</th>}
                                {!pro && <th className='num'>Q3</th>}
                                {!pro && <th className='num'>Q4</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className={`${r.id === highlightId ? 'highlight' : ''} ${!pro && r.poll === 25 ? 'cutoff' : ''}`}>
                                    <td className='num rank-cell'><b>{r.poll}</b> {move(r)}</td>
                                    <td>{teamCell(r)}</td>
                                    <td className='num'>{recordText(r.team.record)}</td>
                                    {pro && <td className='num'>{streakText(r.team.record.streak)}</td>}
                                    {!pro && <td className='num'>{played ? `#${r.rank} (${r.rating.toFixed(1)})` : '-'}</td>}
                                    {!pro && [0, 1, 2, 3].map(i => (
                                        <td key={i} className='num'>{played && r.quads ? `${r.quads[i].w}-${r.quads[i].l}` : '-'}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default RankingsView
